import { EventEmitter } from 'eventemitter3';

/**
 * Simplified MicrophoneService
 * Manages microphone access and audio capture
 */
export class MicrophoneService extends EventEmitter {
  private static instance: MicrophoneService;
  private stream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private processor: ScriptProcessorNode | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private refCount = 0;
  private currentAudioLevel = 0;
  private isCapturing = false;
  private deviceChangeHandler: (() => void) | null = null;
  
  private constructor() {
    super();
    this.setupDeviceChangeMonitoring();
  }
  
  /**
   * Get singleton instance
   */
  public static getInstance(): MicrophoneService {
    if (!MicrophoneService.instance) {
      MicrophoneService.instance = new MicrophoneService();
    }
    return MicrophoneService.instance;
  }
  
  /**
   * Setup monitoring for device changes (microphone plugged/unplugged)
   */
  private setupDeviceChangeMonitoring(): void {
    if (!navigator.mediaDevices) return;
    
    this.deviceChangeHandler = async () => {
      if (this.isCapturing && this.stream) {
        // Check if our audio track is still active
        const tracks = this.stream.getAudioTracks();
        const hasActiveTrack = tracks.some(track => track.readyState === 'live');
        
        if (!hasActiveTrack) {
          this.emit('microphone-lost', {
            reason: 'device-change',
            timestamp: Date.now()
          });
        }
      }
    };
    
    navigator.mediaDevices.addEventListener('devicechange', this.deviceChangeHandler);
  }
  
  /**
   * Check microphone permissions
   */
  public async checkPermissions(): Promise<{ granted: boolean; error?: string; recoveryInstructions?: string }> {
    try {
      // Request permission by trying to get user media
      const testStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        } 
      });
      
      // Permission granted - clean up test stream
      testStream.getTracks().forEach(track => track.stop());
      
      return { granted: true };
    } catch (error) {
      let errorMessage = 'Microphone access denied';
      let recoveryInstructions = 'Please allow microphone access in your browser settings';
      
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Microphone permission denied';
          recoveryInstructions = 'Click the camera icon in your browser\'s address bar to allow microphone access';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No microphone found';
          recoveryInstructions = 'Please connect a microphone and try again';
        } else if (error.name === 'NotReadableError') {
          errorMessage = 'Microphone is in use';
          recoveryInstructions = 'Please close other applications using the microphone';
        }
      }
      
      return { 
        granted: false, 
        error: errorMessage,
        recoveryInstructions 
      };
    }
  }
  
  /**
   * Acquire microphone access (with reference counting)
   */
  public async acquire(): Promise<void> {
    this.refCount++;
    
    if (this.isCapturing) {
      return; // Already capturing
    }
    
    try {
      // Get microphone stream
      this.stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 16000
        } 
      });
      
      // Create audio context at 16kHz
      this.audioContext = new AudioContext({ sampleRate: 16000 });
      
      // Create source from stream
      this.source = this.audioContext.createMediaStreamSource(this.stream);
      
      // Monitor audio track for permission loss or disconnection
      const audioTracks = this.stream.getAudioTracks();
      if (audioTracks.length > 0) {
        const audioTrack = audioTracks[0];

        if (audioTrack != null) {
          // Listen for track ending (permissions revoked, device disconnected, etc.)
          audioTrack.addEventListener('ended', () => {
            this.emit('microphone-lost', {
              reason: 'track-ended',
              timestamp: Date.now()
            });
          });

          // Also monitor readyState changes
          if (audioTrack.readyState === 'ended') {
            // Track already ended before we could add listener
            this.emit('microphone-lost', {
              reason: 'track-already-ended',
              timestamp: Date.now()
            });
          }
        }
      }
      
      // Create script processor for capturing audio data
      // 1024 samples at 16kHz = 64ms chunks
      this.processor = this.audioContext.createScriptProcessor(1024, 1, 1);
      
      // Process audio data
      this.processor.onaudioprocess = (event) => {
        if (!this.isCapturing) return;
        
        const audioData = event.inputBuffer.getChannelData(0);
        
        // Calculate audio level (RMS)
        let sum = 0;

        for (let i = 0; i < audioData.length; i++) {
          const sample = audioData[i] ?? 0;
          sum += sample * sample;
        }
        this.currentAudioLevel = Math.sqrt(sum / audioData.length);
        
        // Emit audio data
        this.emit('audio-data', audioData);
      };
      
      // Connect the audio graph
      this.source.connect(this.processor);
      this.processor.connect(this.audioContext.destination);
      
      this.isCapturing = true;
      
    } catch (error) {
      this.refCount--;
      throw error;
    }
  }
  
  /**
   * Release microphone access (with reference counting)
   */
  public release(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    
    if (this.refCount === 0) {
      this.stopCapture();
    }
  }
  
  /**
   * Stop capturing audio
   */
  private stopCapture(): void {
    this.isCapturing = false;
    
    // Disconnect audio nodes
    if (this.processor) {
      this.processor.disconnect();
      this.processor = null;
    }
    
    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }
    
    // Close audio context
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
      this.audioContext = null;
    }
    
    // Stop media stream tracks
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
    }
    
    this.currentAudioLevel = 0;
  }
  
  /**
   * Cleanup when service is destroyed
   */
  public destroy(): void {
    this.stopCapture();
    
    // Remove device change listener
    if (this.deviceChangeHandler && navigator.mediaDevices) {
      navigator.mediaDevices.removeEventListener('devicechange', this.deviceChangeHandler);
      this.deviceChangeHandler = null;
    }
  }
  
  /**
   * Get current audio level (0-1)
   */
  public getCurrentAudioLevel(): number {
    return Math.min(1, this.currentAudioLevel * 10); // Scale for visibility
  }
  
  /**
   * Check if microphone is muted
   */
  public isMuted(): boolean {
    if (!this.stream) return true;

    const audioTracks = this.stream.getAudioTracks();
    const firstTrack = audioTracks[0];

    return audioTracks.length === 0 || firstTrack == null || !firstTrack.enabled;
  }
  
  /**
   * Set mute state
   */
  public setMuted(muted: boolean): void {
    if (!this.stream) return;
    
    const audioTracks = this.stream.getAudioTracks();
    audioTracks.forEach(track => {
      track.enabled = !muted;
    });
  }
  
  /**
   * Reset service
   */
  public reset(): void {
    this.stopCapture();
    this.refCount = 0;
    this.removeAllListeners();
  }
}